/**
 * Centralized API Configuration for Frontend
 * Handles all API endpoints and base URL management for production deployment
 */

class ApiConfig {
  constructor() {
    // Get base URL from environment variables or default to localhost for development
    const envUrl = import.meta.env.VITE_API_BASE_URL || process.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;

    // If no env URL is set, determine based on current location
    if (!envUrl || envUrl === 'http://localhost:5001') {
      // Use current window location for API calls
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;

      // For local development
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        this.baseUrl = 'http://localhost:5001';
      } else {
        // For production, use nginx proxy - API is available through the main domain
        this.baseUrl = `${protocol}//${hostname}`;
      }
    } else {
      this.baseUrl = envUrl;
    }

    this.apiBaseUrl = `${this.baseUrl}/api`;
  }

  // Authentication endpoints
  auth = {
    login: () => `${this.apiBaseUrl}/auth/login`,
    register: () => `${this.apiBaseUrl}/auth/register`,
    verify: () => `${this.apiBaseUrl}/auth/verify-token`,
    portfolioLogin: () => `${this.apiBaseUrl}/auth/portfolio-login`
  };

  // Assessment endpoints
  assessments = {
    create: () => `${this.apiBaseUrl}/assessments`,
    getById: (id) => `${this.apiBaseUrl}/assessments/${id}`,
    getBySession: (sessionId) => `${this.apiBaseUrl}/assessments/session/${sessionId}`,
    submit: () => `${this.apiBaseUrl}/assessments/submit`,
    responses: (sessionId) => `${this.apiBaseUrl}/assessments/responses/${sessionId}`
  };

  // Business Intelligence endpoints
  businessIntelligence = {
    getAnalysis: (sessionId) => `${this.apiBaseUrl}/business-intelligence/${sessionId}`,
    getRecommendations: (sessionId) => `${this.apiBaseUrl}/business-intelligence/recommendations/${sessionId}`
  };

  // Risk Intelligence endpoints
  riskIntelligence = {
    getFactors: (sessionId) => `${this.apiBaseUrl}/risk-intelligence/factors/${sessionId}`,
    getOutsidePressures: (sessionId) => `${this.apiBaseUrl}/risk-intelligence/outside-pressures/${sessionId}`,
    getRecommendations: () => `${this.apiBaseUrl}/risk-intelligence/recommendations`,
    getFactorRecommendations: (sessionId, factorName) => `${this.apiBaseUrl}/risk-intelligence/factor-recommendations/${sessionId}/${factorName}`
  };

  // Defense Analytics endpoints
  defenseAnalytics = {
    getAnalysis: () => `${this.apiBaseUrl}/defense-analytics`,
    getRecommendations: () => `${this.apiBaseUrl}/defense-analytics/recommendations`,
    getScenarios: () => `${this.apiBaseUrl}/defense-analytics/scenarios`
  };

  // Foundations endpoints
  foundations = {
    getPersonaQuestions: (persona) => `${this.apiBaseUrl}/foundations/persona-questions/${persona}`,
    submitResponse: () => `${this.apiBaseUrl}/foundations/submit-response`,
    getResults: (sessionId) => `${this.apiBaseUrl}/foundations/results/${sessionId}`,
    get360Findings: (sessionId) => `${this.apiBaseUrl}/foundations/360-findings/${sessionId}`,
    getDeepDive: (cardKey) => `${this.apiBaseUrl}/foundations/deep-dive/${cardKey}`,
    getPrismData: (sessionId) => `${this.apiBaseUrl}/foundations/prism-data/${sessionId}`,
    getDashboard: () => `${this.apiBaseUrl}/foundations/dashboard`,
    getPortfolioCompanies: () => `${this.apiBaseUrl}/foundations/portfolio-companies`,
    createCompany: () => `${this.apiBaseUrl}/foundations/create-company`
  };

  // Portfolio endpoints
  portfolio = {
    getCompanies: () => `${this.apiBaseUrl}/portfolio`,
    getCompanyById: (id) => `${this.apiBaseUrl}/portfolio/${id}`,
    createCompany: () => `${this.apiBaseUrl}/portfolio/create-company`,
    getGroups: () => `${this.apiBaseUrl}/portfolio-groups`,
    createGroup: () => `${this.apiBaseUrl}/portfolio-groups/create`
  };

  // Coordinator endpoints
  coordinator = {
    getDashboard: () => `${this.apiBaseUrl}/coordinator/dashboard`,
    getAssessments: () => `${this.apiBaseUrl}/coordinator/assessments`,
    createAssessment: () => `${this.apiBaseUrl}/coordinator/assessments`
  };

  // User management endpoints
  users = {
    create: () => `${this.apiBaseUrl}/users`,
    getById: (id) => `${this.apiBaseUrl}/users/${id}`,
    update: (id) => `${this.apiBaseUrl}/users/${id}`,
    delete: (id) => `${this.apiBaseUrl}/users/${id}`
  };

  // Document management endpoints
  documents = {
    upload: () => `${this.apiBaseUrl}/documents/upload`,
    getById: (id) => `${this.apiBaseUrl}/documents/${id}`,
    delete: (id) => `${this.apiBaseUrl}/documents/${id}`
  };

  // Utility methods
  buildUrl(endpoint) {
    return `${this.apiBaseUrl}${endpoint}`;
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  getApiBaseUrl() {
    return this.apiBaseUrl;
  }
}

// Export singleton instance
export const apiConfig = new ApiConfig();
export default apiConfig;